/**
 * ScorecardPage — J6 (PRD §12.3.9 frontend page #2 + §9.10.2)
 *
 * Cadence Scorecard for the CEO/VP — Recharts visualisations of weekly plan vs
 * shipped (FTT trend), decision latency trend, verification pass rate, and
 * Mura indicators tiles. Sources:
 *   - GET /api/v1/observability/scorecard/ceo  (current snapshot, 14 signals)
 *   - GET /api/v1/observability/trends         (rolling current vs previous)
 *   - GET /api/v1/observability/mura            (FTT/cycle-time CV)
 */

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { Activity, ShieldCheck, GitBranch, Waves, AlertCircle } from 'lucide-react';
import { RorosPage } from '@/components/roros/RorosPage';
import { Card } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { OsHealthTiles } from '@/components/roros/observability/OsHealthTiles';
// D.3 / CLEAN-J6-A3: MuraTile extracted to shared component.
import { MuraTile } from '@/components/roros/dashboards/MuraTile';
import { t } from '@/lib/i18n';
import {
  useRorosCeoScorecard,
  useRorosTrends,
  useRorosMura,
  useRorosHealth,
} from '@/hooks/roros/observability';
import type { RorosHealthSignalDTO, RorosMuraIndicatorDTO } from '@/services/roros/control-tower.service';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getScalar(signals: RorosHealthSignalDTO[] | undefined, key: string): number | null {
  const sig = signals?.find((s) => s.key === key);
  if (!sig || sig.value === null || typeof sig.value !== 'number') return null;
  return sig.value;
}

// ─── Main page ──────────────────────────────────────────────────────────────

export function ScorecardPage() {
  const scorecardQ = useRorosCeoScorecard();
  const trendsQ = useRorosTrends();
  const muraQ = useRorosMura();
  const healthQ = useRorosHealth();

  const signals = (scorecardQ.data ?? []) as RorosHealthSignalDTO[];
  const trends = (trendsQ.data ?? []) as Array<RorosHealthSignalDTO & { trend: { current: number | null; previous: number | null; changePct: number | null } | null }>;
  const mura = muraQ.data as { fttMura: RorosMuraIndicatorDTO | null; cycleTimeMura: RorosMuraIndicatorDTO | null; overallStatus: string } | undefined;
  const allSignals = (healthQ.data ?? []) as RorosHealthSignalDTO[];

  // Derived chart data — current period vs previous, scalar trends
  const trendBarData = React.useMemo(
    () =>
      trends.map((t) => ({
        name: t.label,
        previous: t.trend?.previous ?? 0,
        current: t.trend?.current ?? 0,
      })),
    [trends],
  );

  const fttTrend = trends.find((t) => t.key === 'ftt_system');
  const latencyTrend = trends.find((t) => t.key === 'decision_latency');

  // FTT 90-day "spark" — we don't have a per-day series in the API yet, so we
  // synthesise a 2-point line (previous, current) to give a trend cue. The
  // dedicated 90-day endpoint is a follow-up; this matches what the trends
  // endpoint actually returns today.
  const fttSparkData = React.useMemo(() => {
    if (!fttTrend?.trend) return [];
    return [
      { period: t('Previous'), value: fttTrend.trend.previous ?? 0 },
      { period: t('Current'), value: fttTrend.trend.current ?? 0 },
    ];
  }, [fttTrend]);

  const verificationPassRate = getScalar(signals, 'ftt_system');
  const wipUtilization = getScalar(signals, 'wip_utilization');

  return (
    <RorosPage
      title={t('Cadence Scorecard')}
      breadcrumbs={[{ label: t('Insights'), path: '/roros/observability' }]}
      icon={<Activity className="w-6 h-6" />}
    >
      {(scorecardQ.isError || trendsQ.isError || muraQ.isError) && (
        <Alert variant="error" title={t('Error loading scorecard')} className="mb-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5" aria-hidden />
            <span>{(scorecardQ.error?.message ?? trendsQ.error?.message ?? muraQ.error?.message) ?? ''}</span>
          </div>
        </Alert>
      )}

      {/* Top KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card padding="md" data-testid="scorecard-kpi-ftt">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            <ShieldCheck className="w-3.5 h-3.5" aria-hidden />
            {t('Verification Pass Rate')}
          </div>
          <div className="text-2xl font-semibold text-foreground">
            {verificationPassRate != null ? `${verificationPassRate.toFixed(1)}%` : '—'}
          </div>
        </Card>
        <Card padding="md" data-testid="scorecard-kpi-latency">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            <GitBranch className="w-3.5 h-3.5" aria-hidden />
            {t('Decision Latency')}
          </div>
          <div className="text-2xl font-semibold text-foreground">
            {latencyTrend?.trend?.current != null ? `${latencyTrend.trend.current.toFixed(1)} ${t('days')}` : '—'}
          </div>
        </Card>
        <Card padding="md" data-testid="scorecard-kpi-wip">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            <Activity className="w-3.5 h-3.5" aria-hidden />
            {t('WIP Utilization')}
          </div>
          <div className="text-2xl font-semibold text-foreground">
            {wipUtilization != null ? `${wipUtilization.toFixed(1)}%` : '—'}
          </div>
        </Card>
        <Card padding="md" data-testid="scorecard-kpi-mura">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            <Waves className="w-3.5 h-3.5" aria-hidden />
            {t('Mura Status')}
          </div>
          <div className="text-2xl font-semibold text-foreground">
            {mura ? t(mura.overallStatus) : '—'}
          </div>
        </Card>
      </div>

      {/* Plan vs Shipped (current vs previous bars) */}
      <Card padding="md" className="mb-6" data-testid="scorecard-plan-vs-shipped">
        <h2 className="text-base font-semibold text-foreground mb-3">
          {t('Plan vs Shipped — Current vs Previous Period')}
        </h2>
        {trendBarData.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('No trend data available yet.')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trendBarData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="previous" fill="#cbd5e1" name={t('Previous')} radius={[4, 4, 0, 0]} />
              <Bar dataKey="current" fill="#0ea5e9" name={t('Current')} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* FTT 90-day spark + Decision latency line */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card padding="md" data-testid="scorecard-ftt-trend">
          <h3 className="text-sm font-semibold text-foreground mb-2">{t('First-Time-Through (FTT) Trend')}</h3>
          {fttSparkData.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('Awaiting FTT data.')}</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={fttSparkData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="#22c55e" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card padding="md" data-testid="scorecard-latency-trend">
          <h3 className="text-sm font-semibold text-foreground mb-2">{t('Decision Latency Trend (days)')}</h3>
          {!latencyTrend?.trend ? (
            <p className="text-sm text-muted-foreground">{t('Awaiting decision data.')}</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={[
                  { period: t('Previous'), value: latencyTrend.trend.previous ?? 0 },
                  { period: t('Current'), value: latencyTrend.trend.current ?? 0 },
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="#f97316" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Mura indicators */}
      <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
        <Waves className="w-5 h-5" aria-hidden />
        {t('Mura (Unevenness) Indicators')}
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <MuraTile label={t('FTT Mura')} mura={mura?.fttMura ?? null} />
        <MuraTile label={t('Cycle-Time Mura')} mura={mura?.cycleTimeMura ?? null} />
      </div>

      {/* Full 14 OS Health Signals */}
      <OsHealthTiles signals={allSignals} title={t('All 14 OS Health Signals')} />
    </RorosPage>
  );
}
